import type { FC } from 'react';

import Avatar from '@/common-components/Avatar';
import Pill from '@/common-components/Pill';
import { cn } from '@/utils/cn.util';
import { timeAgo } from '@/utils/date.util';
import type { AdminShouttListItem } from '@/types/moderation.types';

import { SHOUTT_TYPE_PILL } from '../constants';
import ModerationStatusPill from './ModerationStatusPill';

interface ShouttModerationListProps {
  items: AdminShouttListItem[];
  mutatingId: string | null;
  onView: (item: AdminShouttListItem) => void;
  onApprove: (item: AdminShouttListItem) => void;
  onOpenReject: (item: AdminShouttListItem) => void;
  onRemove: (item: AdminShouttListItem) => void;
  onRestore: (item: AdminShouttListItem) => void;
}

const actionBtn =
  'cursor-pointer whitespace-nowrap rounded-lg px-3.5 py-[7px] text-[12px] font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-50';

const isPulled = (item: AdminShouttListItem): boolean =>
  item.status === 'removed' || item.moderation.state === 'rejected';

/** Queue rows ported from the prototype's .mod-item cards. */
const ShouttModerationList: FC<ShouttModerationListProps> = ({
  items,
  mutatingId,
  onView,
  onApprove,
  onOpenReject,
  onRemove,
  onRestore,
}) => (
  <div className="flex flex-col gap-2.5">
    {items.map((item) => {
      const mutating = mutatingId === item.id;
      const typePill = SHOUTT_TYPE_PILL[item.type];
      const pulled = isPulled(item);

      return (
        <div
          key={item.id}
          className={cn(
            'flex items-start gap-3.5 rounded-xl border border-[var(--color-divider)] bg-white p-4 transition-all hover:shadow-[var(--shadow-card)]',
            item.moderation.aiFlagged && 'border-l-[3px] border-l-[var(--color-danger)]',
            !item.moderation.aiFlagged &&
              item.moderation.reportCount > 0 &&
              'border-l-[3px] border-l-[var(--color-warn)]',
          )}
        >
          <Avatar name={item.author.name} src={item.author.avatarUrl} size="lg" />

          <div className="min-w-0 flex-1">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <span className="text-[13.5px] font-semibold text-[var(--color-ink)]">
                {item.author.name}
              </span>
              <Pill tone={typePill.tone} className="text-[10px]">
                {typePill.label}
              </Pill>
              {item.moderation.aiFlagged ? (
                <Pill tone="danger" className="text-[10px]">
                  🤖 AI Flag
                </Pill>
              ) : null}
              {item.moderation.reportCount > 0 ? (
                <Pill tone="warn" className="text-[10px]">
                  Reported ({item.moderation.reportCount}x)
                </Pill>
              ) : null}
            </div>

            <p className="mb-2 text-[12.5px] leading-normal text-[var(--color-ink-muted)]">
              “{item.body}”
            </p>

            <div className="flex flex-wrap items-center gap-1.5">
              <ModerationStatusPill kind="shouttStatus" value={item.status} />
              {item.moderation.state !== 'approved' ? (
                <ModerationStatusPill kind="moderationState" value={item.moderation.state} />
              ) : null}
              <span className="text-[11px] text-[var(--color-ink-soft)]">
                {item.campusName} · {timeAgo(item.createdAt)}
              </span>
            </div>
          </div>

          <div className="flex shrink-0 flex-col gap-1.5">
            {pulled ? (
              <button
                type="button"
                disabled={mutating}
                onClick={() => onRestore(item)}
                className={cn(
                  actionBtn,
                  'bg-[var(--color-success-bg)] text-[var(--color-success-ink)] hover:bg-[var(--color-success)] hover:text-white',
                )}
              >
                {mutating ? 'Restoring…' : '↩ Restore'}
              </button>
            ) : (
              <>
                {item.moderation.state === 'pending' ? (
                  <button
                    type="button"
                    disabled={mutating}
                    onClick={() => onApprove(item)}
                    className={cn(
                      actionBtn,
                      'bg-[var(--color-success-bg)] text-[var(--color-success-ink)] hover:bg-[var(--color-success)] hover:text-white',
                    )}
                  >
                    ✓ Approve
                  </button>
                ) : null}
                <button
                  type="button"
                  disabled={mutating}
                  onClick={() => onOpenReject(item)}
                  className={cn(
                    actionBtn,
                    'bg-[var(--color-danger-bg)] text-[var(--color-danger-ink)] hover:bg-[var(--color-danger)] hover:text-white',
                  )}
                >
                  ✗ Reject
                </button>
                <button
                  type="button"
                  disabled={mutating}
                  onClick={() => onRemove(item)}
                  className={cn(
                    actionBtn,
                    'border border-[var(--color-divider)] bg-[var(--color-surface-muted)] text-[var(--color-ink-muted)] hover:bg-[var(--color-surface-muted-2)]',
                  )}
                >
                  {mutating ? 'Working…' : '🗑 Remove'}
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => onView(item)}
              className={cn(
                actionBtn,
                'border border-[var(--color-divider)] bg-white text-[var(--color-ink-muted)] hover:bg-[var(--color-surface-muted)]',
              )}
            >
              Details
            </button>
          </div>
        </div>
      );
    })}
  </div>
);

export default ShouttModerationList;
